/* eslint-disable @typescript-eslint/naming-convention */
import {
  HeaderCellConfiguration,
  PaginationDefaults,
  WcgAdvancedFilterType,
  WcgTableData,
  WcgMenuOption,
} from '@wcg/frontend-framework';

import {
  DocumentTypeState,
  SDocument,
  SubmissionTabs,
  SubmissionTypeState,
} from '../types';
import { ActivatedRouteSnapshot } from '@angular/router';
import environment from '@environments/environment';
import { WcgTableValues } from '@shared/interfaces/wcg-table-utils.interface';

export const ConfigurationFeatureKey = 'configurations';

export const configurationDocumentKey = 'configurations-document';
export const configurationSubmissionKey = 'configurations-submission';

export const DEFAULT_SUBMISSION_TABS: SubmissionTabs[] = [
  {
    name: 'Document Types',
    templateName: 'documentTypes',
  },
  {
    name: 'IRIS Settings',
    templateName: 'irisSettings',
  },
];

export const DEFAULT_DOCUMENT_TYPES_ADVANCE_FILTER = {
  name: {
    columnKey: 'name',
    label: 'Document Type Name',
    type: WcgAdvancedFilterType.StringSearch,
  },
  is_active: {
    columnKey: 'is_active',
    label: 'Enabled',
    type: WcgAdvancedFilterType.SingleSelect,
    options: [],
  },
  legacy_id: {
    columnKey: 'legacy_id',
    label: 'IRIS Document ID',
    type: WcgAdvancedFilterType.StringSearch,
  },
};

export const DEFAULT_DOCUMENT_ENTITY_TYPE = ['Study', 'Site'];

export const DOCUMENT_TYPES_COLUMNS: HeaderCellConfiguration[] = [
  {
    cellData: 'Actions',
    columnKey: 'action',
    componentConfig: {
      blankHeader: true,
      draggable: false,
      showColumn: true,
      sortable: false,
      sticky: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Document Type Name',
    componentType: 'default',
    componentConfig: {
      sticky: true,
      field: 'name',
      showColumn: true,
    },
    columnKey: 'name',
  },
  {
    cellData: 'Enabled',
    componentType: 'default',
    componentConfig: {
      field: 'is_active',
      showColumn: true,
    },
    columnKey: 'is_active',
  },
  {
    cellData: 'IRIS Document ID',
    componentType: 'default',
    componentConfig: {
      field: 'legacy_id',
      showColumn: true,
    },
    columnKey: 'legacy_id',
  },
];

export const DEFAULT_TABLE_PARAMS: WcgTableValues = {
  filters: [],
  page: 1,
  pageSize: 25,
  searchValue: '',
  sortParam: {
    dir: 'asc',
    field: '',
  },
};

export const DEFAULT_DOCUMENT_TYPES_TABLE_DATA: WcgTableData = {
  filters: {
    advancedFilters: DEFAULT_DOCUMENT_TYPES_ADVANCE_FILTER,
    searchString: '',
    searchableColumns: DOCUMENT_TYPES_COLUMNS.filter(
      (header) => header.cellData !== 'Actions',
    ).map((header) => header.cellData),
  },
  headers: DOCUMENT_TYPES_COLUMNS,
  pagination: { ...PaginationDefaults },
  rows: [],
  title: 'Document Types',
};

export const CONFIG_SUBMISSIONS_COLUMNS: HeaderCellConfiguration[] = [
  {
    cellData: 'Submission Type Name',
    columnKey: 'label',
    componentConfig: {
      field: 'label',
      showColumn: true,
      sticky: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Submission Type Short Name',
    columnKey: 'name',
    componentConfig: {
      field: 'name',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Entity Type',
    columnKey: 'entity_type',
    componentConfig: {
      field: 'entity_type',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Status',
    columnKey: 'configuration_status',
    componentConfig: {
      field: 'configuration_status',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Created On',
    columnKey: 'configuration_created_dt',
    componentConfig: {
      field: 'configuration_created_dt',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Published On',
    columnKey: 'configuration_published_dt',
    componentConfig: {
      field: 'configuration_published_dt',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'Origin',
    columnKey: 'origin',
    componentConfig: {
      field: 'origin',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'No. of Document Types',
    columnKey: 'num_document_types',
    componentConfig: {
      field: 'num_document_types',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'IRIS Routing Group',
    columnKey: 'iris_routing_group_name',
    componentConfig: {
      field: 'iris_routing_group_name',
      showColumn: true,
    },
    componentType: 'default',
  },
  {
    cellData: 'IRIS Envelope Type',
    columnKey: 'iris_envelope_type',
    componentConfig: {
      field: 'iris_envelope_type',
      showColumn: true,
    },
    componentType: 'default',
  },
];

export const DEFAULT_SUBMISSIONS_TYPES_ADVANCE_FILTER = {
  label: {
    columnKey: 'label',
    label: 'Submission Type Name',
    type: WcgAdvancedFilterType.StringSearch,
  },
  name: {
    columnKey: 'name',
    label: 'Submission Type Short Name',
    type: WcgAdvancedFilterType.MultiSelect,
    options: [],
  },
  entity_type: {
    columnKey: 'entity_type',
    label: 'Entity Type',
    type: WcgAdvancedFilterType.SingleSelect,
    options: [
      { label: 'Site', value: 'site' },
      { label: 'Study', value: 'study' },
    ],
  },
  origin: {
    columnKey: 'origin',
    label: 'Origin',
    type: WcgAdvancedFilterType.SingleSelect,
    options: [
      { label: 'eRM', value: 'eRM' },
      { label: 'non-eRM', value: 'non-eRM' },
    ],
  },
  num_document_types: {
    columnKey: 'num_document_types',
    label: 'No. of Document Types',
    type: WcgAdvancedFilterType.StringSearch,
  },
  iris_routing_group_name: {
    columnKey: 'iris_routing_group_name',
    label: 'IRIS Routing Group',
    type: WcgAdvancedFilterType.MultiSelect,
    options: [],
  },
  iris_envelope_type: {
    columnKey: 'iris_envelope_type',
    label: 'IRIS Envelope Type',
    type: WcgAdvancedFilterType.StringSearch,
    options: [],
  },
  configuration_status: {
    columnKey: 'configuration_status',
    label: 'Status',
    type: WcgAdvancedFilterType.SingleSelect,
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Published', value: 'published' },
    ],
  },
  configuration_created_dt: {
    columnKey: 'configuration_created_dt',
    label: 'Created On',
    type: WcgAdvancedFilterType.DateRange,
  },
  configuration_published_dt: {
    columnKey: 'configuration_published_dt',
    label: 'Published On',
    type: WcgAdvancedFilterType.DateRange,
  },
};

export const DEFAULT_SUBMISSIONS_TABLE_DATA: WcgTableData = {
  filters: {
    advancedFilters: DEFAULT_SUBMISSIONS_TYPES_ADVANCE_FILTER,
    searchString: '',
    searchableColumns: CONFIG_SUBMISSIONS_COLUMNS.filter(
      (header) => header.cellData !== 'Actions',
    ).map((header) => header.cellData),
  },
  headers: CONFIG_SUBMISSIONS_COLUMNS,
  pagination: { ...PaginationDefaults },
  rows: [],
  title: 'Submissions',
};

export const DEFAULT_DOCUMENT_ATTRIBUTES_HTTP_PARAMS = {
  page: 1,
  page_size: 100,
  query: '',
};

export const DEFAULT_SUBMISSION_DOCUMENT: SDocument = {
  data: {
    entity: [],
    mapping_id: 0,
    order: 0,
  },
  description: '',
  document_types: [],
  id: '',
  iris_mapping: {
    envelope_type: {
      name: '',
    },
    routing_group: {
      name: '',
      id: 0,
    },
  },
  name: '',
};

export const DEFAULT_IRIS_ROUTING_GROUPS: WcgMenuOption[] = [
  {
    id: '14',
    label: 'CIR Board Specialists',
  },
  {
    id: '4',
    label: 'Continuing Review',
  },
  {
    id: '73',
    label: 'Demographics',
  },
  {
    id: '114',
    label: 'Federal-Institution',
  },
  {
    id: '9',
    label: 'Log in Specialists',
  },
  {
    id: '13',
    label: 'Post-Board Specialists',
  },
  {
    id: '89',
    label: 'Regulatory Letters',
  },
  {
    id: '97',
    label: 'Renewal',
  },
  {
    id: '56',
    label: 'Study Closure',
  },
  {
    id: '62',
    label: 'Team 1',
  },
  {
    id: '18',
    label: 'Translations',
  },
  {
    id: '117',
    label: 'Unanticipated Problems',
  },
];

export const DEFAULT_DOCUMENT_TYPE_STATE: DocumentTypeState = {
  attributes: {
    data: [],
    total: 0,
  },
  documentTypes: {
    data: [],
    is_active: [],
    loading: false,
    total: 0,
  },
  documentTypesTableData: {
    filters: {
      advancedFilters: DEFAULT_DOCUMENT_TYPES_ADVANCE_FILTER,
      searchString: '',
      searchableColumns: DOCUMENT_TYPES_COLUMNS.filter(
        (header) => header.cellData !== 'Actions',
      ).map((header) => header.cellData),
    },
    headers: DOCUMENT_TYPES_COLUMNS,
    pagination: { ...PaginationDefaults },
    rows: [],
    title: 'Document Types',
  },
};
export const DEFAULT_SUBMISSION_TYPE_STATE: SubmissionTypeState = {
  submissionDocument: {
    ...DEFAULT_SUBMISSION_DOCUMENT,
    parent: DEFAULT_SUBMISSION_DOCUMENT,
    parent_id: '',
  },
  submissions: {
    data: [],
    loading: false, // Default value for total count
    total: 0,
  },
  submissionsTableData: {
    filters: {
      advancedFilters: DEFAULT_SUBMISSIONS_TYPES_ADVANCE_FILTER,
      searchString: '',
      searchableColumns: CONFIG_SUBMISSIONS_COLUMNS.filter(
        (header) => header.cellData !== 'Actions',
      ).map((header) => header.cellData),
    },
    headers: CONFIG_SUBMISSIONS_COLUMNS,
    pagination: { ...PaginationDefaults },
    rows: [],
    title: 'Submission Types',
  },
};

export const CONFIGURE_DEFAULT_PARAMS = (
  route?: ActivatedRouteSnapshot,
): WcgTableValues => {
  return {
    filters: route?.queryParams['filters'] ?? '',
    page: route?.queryParams['pageIndex'] ?? 1,
    pageSize: route?.queryParams['pageSize'] ?? 25,
    searchValue: route?.queryParams['searchValue'] ?? '',
    sortParam: route?.queryParams['sortParam'] ?? '',
  };
};

export const DOCUMENT_TYPE_URL = `${environment.irisApi}/admin/document/type/`;
export const DOCUMENT_TYPE_URL_V2 = `${environment.irisApi}/v2/admin/document/type/`;
export const ADMIN_WORKSPACE_URL = `${environment.irisApi}/admin/workspace/type/`;
export const ADMIN_URL_V2 = `${environment.irisApi}/v2/admin`;
export const ADMIN_WORKSPACE_URL_V2 = `${ADMIN_URL_V2}/workspace/type/`;
export const WORKSPACE_SETUP_URL = `${environment.irisApi}/v2/workspace/setup`;
